// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { checkoutSharedStyles } from "@sc/App/SharedStyles/CheckoutSharedStyles";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationPaymentMethodStyles {
    container?: InjectableCss;
    paymentMethodTitleText?: TypographyPresentationProps;
    paymentMethodText?: TypographyPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    poWrapper?: InjectableCss;
    paymentMethodWrapper?: InjectableCss;
}

export const paymentMethodStyles: OnePageCheckoutConfirmationPaymentMethodStyles = {
    container: {
        css: css`
            flex-direction: column;
            margin-top: 15px;
        `,
    },
    paymentMethodTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = paymentMethodStyles;

const OnePageCheckoutConfirmationPaymentMethod = ({ cart }: Props) => {
    if (!cart || cart.requiresApproval) {
        return null;
    }

    // XNG-Change: change on structure to match design
    return (
        <>
            {/* XNG-Change */}
            <Typography as="h3" {...styles.title}>
                {translate("Payment")}
            </Typography>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.paymentMethodWrapper}>
                    <SmallHeadingAndText
                        {...checkoutSharedStyles.headingAndTextStyles}
                        heading={translate("Payment Method")}
                        text={cart.paymentMethod?.description || cart.paymentMethod?.name || ""}
                    />
                </StyledWrapper>
                {/* XNG-Change: display po */}
                {cart.poNumber && (
                    <StyledWrapper {...styles.poWrapper}>
                        <SmallHeadingAndText
                            {...checkoutSharedStyles.headingAndTextStyles}
                            heading={translate("Purchase Order #")}
                            text={cart.poNumber}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationPaymentMethod),
    definition: {
        displayName: "Payment Method",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
